import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Loader2, MessageSquare, Sparkles, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// WhatsApp admin link - use the official QR link provided by admin
const WHATSAPP_ADMIN_LINK = "https://wa.me/14168465062";

// Convert URLs in text to clickable links
const linkifyText = (text) => {
  if (!text) return null;
  
  // Regex to match URLs
  const urlRegex = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
  const parts = text.split(urlRegex);
  
  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      // Reset regex lastIndex
      urlRegex.lastIndex = 0;
      const isWhatsApp = part.includes('wa.me');
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-1 underline font-medium ${
            isWhatsApp 
              ? 'text-green-600 hover:text-green-700' 
              : 'text-indigo-600 hover:text-indigo-700'
          }`}
        >
          {isWhatsApp ? 'WhatsApp Link' : part.length > 40 ? part.substring(0, 40) + '...' : part}
          <ExternalLink className="w-3 h-3 inline" />
        </a>
      );
    }
    return part;
  });
};

// Responsive positioning helper
const getResponsiveStyles = (isMobile) => ({
  button: {
    position: 'fixed',
    bottom: isMobile ? '20px' : '28px',
    right: isMobile ? '16px' : '28px',
    zIndex: 9999,
    width: isMobile ? '64px' : '72px',
    height: isMobile ? '64px' : '72px',
  },
  window: {
    position: 'fixed',
    bottom: isMobile ? '20px' : '28px',
    right: isMobile ? '16px' : '28px',
    zIndex: 9999,
    width: isMobile ? 'calc(100vw - 32px)' : '380px',
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: isMobile ? 'calc(100vh - 40px)' : 'calc(100vh - 56px)',
  },
  greeting: {
    position: 'fixed',
    bottom: isMobile ? '100px' : '120px',
    right: isMobile ? '16px' : '28px',
    zIndex: 9999,
    maxWidth: isMobile ? 'calc(100vw - 32px)' : '300px',
  }
});

const AIChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showGreeting, setShowGreeting] = useState(false);
  const [qualifiedLead, setQualifiedLead] = useState(null);
  const [collectedInfo, setCollectedInfo] = useState({});
  const [readyForHandoff, setReadyForHandoff] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showAttentionBubble, setShowAttentionBubble] = useState(false);
  const [attentionMessage, setAttentionMessage] = useState("👋 Hi! Need help finding a tutor?");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Rotating attention messages
  const attentionMessages = [
    "👋 Hi! Need help finding a tutor?",
    "📚 Need help with Math or Science?",
    "🎓 Looking for a tutor?",
    "💬 Ask our AI Assistant!"
  ];

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const styles = getResponsiveStyles(isMobile);

  // Show attention bubble on load
  useEffect(() => {
    // Initial show after 3 seconds
    const initialTimer = setTimeout(() => {
      if (!isOpen) {
        setShowAttentionBubble(true);
        // Auto-hide after 6 seconds
        setTimeout(() => setShowAttentionBubble(false), 6000);
      }
    }, 3000);

    // Rotate messages every 45 seconds
    let messageIndex = 0;
    const rotationInterval = setInterval(() => {
      if (!isOpen) {
        messageIndex = (messageIndex + 1) % attentionMessages.length;
        setAttentionMessage(attentionMessages[messageIndex]);
        setShowAttentionBubble(true);
        // Auto-hide after 5 seconds
        setTimeout(() => setShowAttentionBubble(false), 5000);
      }
    }, 45000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(rotationInterval);
    };
  }, [isOpen]);

  // Hide attention bubble when chat opens
  useEffect(() => {
    if (isOpen) {
      setShowAttentionBubble(false);
    }
  }, [isOpen]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const startSession = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ai-assistant/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSessionId(data.session_id);
        setMessages([{
          role: 'assistant',
          content: data.greeting
        }]);
      }
    } catch (error) {
      console.error('Error starting AI session:', error);
      setMessages([{
        role: 'assistant',
        content: "Hi! 👋 I'm the Gifted Academy AI Assistant. How can I help you today?"
      }]);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    if (!sessionId) {
      startSession();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  // Gather website context for AI - captures what user is currently viewing
  const getWebsiteContext = () => {
    const context = [];
    
    // Current page info
    context.push(`Current page: ${window.location.pathname}`);
    context.push(`Page title: ${document.title}`);
    
    // Check what section user is viewing (based on scroll position)
    const sections = document.querySelectorAll('section[id], [data-section]');
    const visibleSections = [];
    sections.forEach(section => {
      const rect = section.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        const sectionName = section.id || section.getAttribute('data-section');
        if (sectionName) visibleSections.push(sectionName);
      }
    });
    if (visibleSections.length > 0) {
      context.push(`Visible sections: ${visibleSections.join(', ')}`);
    }
    
    // Capture subjects from the Subjects section if visible
    const subjectsSection = document.getElementById('subjects');
    if (subjectsSection) {
      const rect = subjectsSection.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        // Look for subject buttons or cards
        const subjectElements = subjectsSection.querySelectorAll('[data-subject], .subject-card, button, span');
        const subjects = new Set();
        subjectElements.forEach(el => {
          const subject = el.getAttribute('data-subject') || el.textContent?.trim();
          if (subject && subject.length < 30 && !subject.includes('\n')) {
            // Common STEM subjects
            const stemSubjects = ['Mathematics', 'Physics', 'Chemistry', 'Calculus', 'Linear Algebra', 'Statistics', 'Computer Science', 'Biology', 'Data Management', 'Functions', 'Advanced Functions'];
            if (stemSubjects.some(s => subject.toLowerCase().includes(s.toLowerCase()))) {
              subjects.add(subject);
            }
          }
        });
        if (subjects.size > 0) {
          context.push(`Subjects displayed on website: ${Array.from(subjects).join(', ')}`);
        }
      }
    }
    
    // Capture pricing calculator state
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      const rect = pricingSection.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        // Look for selected pricing elements
        const activeButtons = pricingSection.querySelectorAll('[data-state="active"], .active, [aria-pressed="true"], .selected');
        const pricingContext = [];
        activeButtons.forEach(btn => {
          const text = btn.textContent?.trim();
          if (text && text.length < 50) {
            pricingContext.push(text);
          }
        });
        
        // Look for price displays
        const priceElements = pricingSection.querySelectorAll('[data-price], .price');
        priceElements.forEach(el => {
          const price = el.getAttribute('data-price') || el.textContent?.trim();
          if (price && price.includes('$')) {
            pricingContext.push(`Price shown: ${price}`);
          }
        });
        
        if (pricingContext.length > 0) {
          context.push(`Pricing calculator state: ${pricingContext.join(', ')}`);
        }
      }
    }
    
    // Check for data attributes on active elements
    const pricingLevel = document.querySelector('[data-pricing-level]');
    const pricingHours = document.querySelector('[data-pricing-hours]');
    const pricingType = document.querySelector('[data-pricing-type]');
    if (pricingLevel) context.push(`Selected level: ${pricingLevel.getAttribute('data-pricing-level')}`);
    if (pricingHours) context.push(`Selected hours: ${pricingHours.getAttribute('data-pricing-hours')}`);
    if (pricingType) context.push(`Student type: ${pricingType.getAttribute('data-pricing-type')}`);
    
    return context.length > 1 ? context.join('\n') : null;
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputValue.trim() || isLoading) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    
    // Add user message to chat
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    
    try {
      // Start session if not exists
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        const startResponse = await fetch(`${API_URL}/api/ai-assistant/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
        if (startResponse.ok) {
          const startData = await startResponse.json();
          currentSessionId = startData.session_id;
          setSessionId(currentSessionId);
        }
      }
      
      // Gather website context
      const websiteContext = getWebsiteContext();
      
      const response = await fetch(`${API_URL}/api/ai-assistant/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: currentSessionId,
          message: userMessage,
          website_context: websiteContext
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: data.response 
        }]);
        
        // Track collected info from session
        if (data.collected_info) {
          setCollectedInfo(prev => ({ ...prev, ...data.collected_info }));
        }
        
        // Check if lead is qualified and merge with collected info
        if (data.lead_info) {
          setQualifiedLead(prev => prev ? { ...prev, ...data.lead_info } : data.lead_info);
        }
        
        // Only show WhatsApp CTA when truly qualified (has explicit qualified_lead flag)
        if (data.qualified_lead === true) {
          setReadyForHandoff(true);
        }
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: "I'm sorry, I'm having trouble right now. Please try again or contact us on WhatsApp."
        }]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, something went wrong. Please try again or reach out to us on WhatsApp."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWhatsAppHandoff = () => {
    // Build lead summary for display (QR links don't support prefilled text)
    const allInfo = { ...collectedInfo, ...qualifiedLead };
    
    // Build summary text to show user before handoff
    let summaryLines = [];
    if (allInfo.name) summaryLines.push(`Name: ${allInfo.name}`);
    if (allInfo.role && allInfo.role !== allInfo.name) summaryLines.push(`Parent: ${allInfo.role}`);
    if (allInfo.student_level) summaryLines.push(`Level: ${allInfo.student_level}`);
    if (allInfo.grade) summaryLines.push(`Grade: ${allInfo.grade}`);
    if (allInfo.subjects || allInfo.subject) summaryLines.push(`Subject: ${allInfo.subjects || allInfo.subject}`);
    if (allInfo.urgency) summaryLines.push(`Urgency: ${allInfo.urgency}`);
    if (allInfo.email) summaryLines.push(`Email: ${allInfo.email}`);
    if (allInfo.phone) summaryLines.push(`Phone: ${allInfo.phone}`);
    
    // If we have collected info, add a message showing what will be shared
    if (summaryLines.length > 0) {
      const summary = summaryLines.join('\n');
      // Copy summary to clipboard for easy pasting
      navigator.clipboard?.writeText(
        `Hi, I'm interested in tutoring at Gifted Academy.\n\n${summary}\n\nI came from the website chatbot.`
      ).catch(() => {});
    }
    
    // Open the official WhatsApp QR link
    window.open(WHATSAPP_ADMIN_LINK, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      {/* Floating Communication Buttons - Stacked on Right Side */}
      {!isOpen && (
        <div 
          style={{
            position: 'fixed',
            bottom: isMobile ? '20px' : '28px',
            right: isMobile ? '16px' : '28px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: isMobile ? '12px' : '16px',
            zIndex: 9998
          }}
        >
          {/* Welcome Bubble - Shows above AI button */}
          <AnimatePresence>
            {showAttentionBubble && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.9 }}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: isMobile ? '10px 14px' : '12px 16px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                  maxWidth: isMobile ? '200px' : '220px',
                  marginBottom: '8px'
                }}
              >
                <p style={{ 
                  margin: 0, 
                  fontSize: isMobile ? '13px' : '14px', 
                  color: '#374151',
                  fontWeight: 500
                }}>
                  {attentionMessage}
                </p>
                <div 
                  style={{
                    position: 'absolute',
                    bottom: '-6px',
                    right: '28px',
                    width: '12px',
                    height: '12px',
                    background: 'white',
                    transform: 'rotate(45deg)',
                    boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* AI Assistant Button - Top */}
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ 
              scale: 1, 
              opacity: 1,
              y: [0, -4, 0]
            }}
            transition={{
              scale: { duration: 0.3 },
              y: { duration: 3, repeat: Infinity, ease: "easeInOut" }
            }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleOpen}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: isMobile ? '12px 16px' : '14px 20px',
              borderRadius: '50px',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
              boxShadow: '0 0 20px rgba(139, 92, 246, 0.5), 0 0 40px rgba(139, 92, 246, 0.3), 0 8px 24px rgba(0, 0, 0, 0.15)',
              cursor: 'pointer',
              border: 'none',
              color: 'white',
              fontWeight: 600,
              fontSize: isMobile ? '13px' : '14px',
              whiteSpace: 'nowrap'
            }}
            data-testid="ai-chat-btn"
            aria-label="Ask AI Assistant"
          >
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Sparkles style={{ width: isMobile ? '18px' : '20px', height: isMobile ? '18px' : '20px' }} />
              <motion.div
                animate={{ 
                  scale: [1, 1.4, 1],
                  opacity: [0.7, 1, 0.7]
                }}
                transition={{ duration: 2, repeat: Infinity }}
                style={{
                  position: 'absolute',
                  top: '-3px',
                  right: '-3px',
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#4ade80',
                  borderRadius: '50%',
                  border: '1.5px solid white'
                }}
              />
            </div>
            <span>Ask AI Assistant</span>
          </motion.button>

          {/* WhatsApp Button - Bottom */}
          <motion.a
            href={WHATSAPP_ADMIN_LINK}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ 
              scale: 1, 
              opacity: 1,
              y: [0, -3, 0]
            }}
            transition={{
              scale: { duration: 0.3, delay: 0.1 },
              y: { duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }
            }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: isMobile ? '12px 16px' : '14px 20px',
              borderRadius: '50px',
              background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
              boxShadow: '0 0 20px rgba(37, 211, 102, 0.4), 0 0 40px rgba(37, 211, 102, 0.2), 0 8px 24px rgba(0, 0, 0, 0.15)',
              cursor: 'pointer',
              border: 'none',
              color: 'white',
              fontWeight: 600,
              fontSize: isMobile ? '13px' : '14px',
              textDecoration: 'none',
              whiteSpace: 'nowrap'
            }}
            data-testid="whatsapp-float-btn"
            aria-label="Chat with Admin on WhatsApp"
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="white" 
              style={{ width: isMobile ? '18px' : '20px', height: isMobile ? '18px' : '20px' }}
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            <span>Chat with Admin</span>
          </motion.a>
        </div>
      )}

      {/* Chat Window - Floating panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            style={{
              ...styles.window,
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
            data-testid="ai-chat-window"
          >
            {/* Header */}
            <div 
              className="p-4 flex items-center justify-between text-white"
              style={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)'
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">AI Assistant</h3>
                  <p className="text-xs text-white/80">Powered by Gifted Academy</p>
                </div>
              </div>
              <button 
                onClick={handleClose}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                data-testid="close-ai-chat"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Messages Area */}
            <div className="h-[350px] overflow-y-auto p-4 space-y-3 bg-slate-50">
              {messages.map((msg, index) => (
                <div 
                  key={index}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div 
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                      msg.role === 'user'
                        ? 'bg-indigo-600 text-white rounded-br-sm'
                        : 'bg-white border border-slate-200 text-slate-700 rounded-bl-sm shadow-sm'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">
                      {msg.role === 'assistant' ? linkifyText(msg.content) : msg.content}
                    </p>
                  </div>
                </div>
              ))}
              
              {/* Loading indicator */}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
                      <span className="text-sm text-slate-500">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* WhatsApp Handoff Card - Only show when truly qualified */}
              {readyForHandoff && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    <span className="font-medium text-green-800 text-sm">Ready to continue?</span>
                  </div>
                  <p className="text-xs text-green-700 mb-3">
                    Connect with our admin on WhatsApp to discuss pricing, schedule, and get started!
                  </p>
                  <Button
                    onClick={handleWhatsAppHandoff}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                    size="sm"
                    data-testid="whatsapp-handoff-btn"
                  >
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    Continue with Admin on WhatsApp
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <form onSubmit={sendMessage} className="p-4 border-t border-slate-200 bg-white">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  placeholder="Type your message..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  className="flex-1"
                  disabled={isLoading}
                  data-testid="ai-chat-input"
                />
                <Button 
                  type="submit" 
                  disabled={isLoading || !inputValue.trim()}
                  className="bg-indigo-600 hover:bg-indigo-700 px-4"
                  data-testid="ai-chat-send"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
              
              {/* Quick actions */}
              <div className="flex gap-2 mt-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setInputValue("What subjects do you offer?")}
                  className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-600 transition-colors"
                >
                  Subjects offered
                </button>
                <button
                  type="button"
                  onClick={() => setInputValue("How do I get started?")}
                  className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-600 transition-colors"
                >
                  Get started
                </button>
                <button
                  type="button"
                  onClick={() => setInputValue("I'd like to book a tutor")}
                  className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-600 transition-colors"
                >
                  Book a tutor
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIChatWidget;
